import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Trans, useTranslation } from "react-i18next";

import { Modal } from "components/Modal/Modal";
import { TrackingContext } from "context/Tracking";
import { RiskGroup } from "pages/Risk/hooks/useRiskGroups";
import { PrimaryButton } from "components/Button/PrimaryButton";
import { calculateScore } from "components/Risk/utils";

import { validateDraft } from "./validateDraft";
import { useAcceptAIHazard } from "./hooks/useAcceptAIHazard";
import { WarningsPanel, hasAcknowledgeableWarnings } from "./WarningsPanel";
import { AIDraftRisk, AIHazardShell } from "./types";

const splitMultiValue = (value: string | undefined): string[] =>
  (value || "").split(";").filter((part) => part !== "");

interface DraftSummaryProps {
  draft: AIDraftRisk;
}

// Read-only render of EVERYTHING being accepted: name, classification, score
// before/after, every mitigation with type/description/left_risk/score
// (plan §8 step 1). Also reused by the accepted-readonly detail state.
export const DraftSummary = ({ draft }: DraftSummaryProps) => {
  const { t } = useTranslation();

  const initialScore = calculateScore(draft);
  const finalScore = draft.mitigations.length
    ? calculateScore(draft.mitigations[draft.mitigations.length - 1])
    : initialScore;

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium text-gray-900">
          {draft.readable_name}
        </p>
        <p className="mt-1 text-sm text-gray-600 whitespace-pre-line">
          {draft.description}
        </p>
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
        <div className="text-sm">
          <dt className="inline font-medium text-gray-500">
            {t("form.risk.group")}
            {": "}
          </dt>
          <dd className="inline text-gray-800">
            {t(`form.risk.options.group.${draft.group_id}`)}
          </dd>
        </div>
        <div className="text-sm">
          <dt className="inline font-medium text-gray-500">
            {t("form.risk.origin")}
            {": "}
          </dt>
          <dd className="inline text-gray-800">
            {t(`form.risk.options.origin.${draft.origin_id}`)}
          </dd>
        </div>
        <div className="text-sm">
          <dt className="inline font-medium text-gray-500">
            {t("form.risk.consequence")}
            {": "}
          </dt>
          <dd className="inline text-gray-800">
            {splitMultiValue(draft.consequence_id)
              .map((value) => t(`form.risk.options.consequence.${value}`))
              .join(", ")}
          </dd>
        </div>
        <div className="text-sm">
          <dt className="inline font-medium text-gray-500">
            {t("form.risk.phase")}
            {": "}
          </dt>
          <dd className="inline text-gray-800">
            {splitMultiValue(draft.phase_id)
              .map((value) => t(`form.risk.options.phase.${value}`))
              .join(", ")}
          </dd>
        </div>
        <div className="text-sm">
          <dt className="inline font-medium text-gray-500">
            {t("form.risk.protection_goal")}
            {": "}
          </dt>
          <dd className="inline text-gray-800">{draft.protection_goal}</dd>
        </div>
        <div className="text-sm">
          <dt className="inline font-medium text-gray-500">
            {t("form.risk.personal")}
            {": "}
          </dt>
          <dd className="inline text-gray-800">{draft.personal}</dd>
        </div>
      </dl>

      <p className="text-sm text-gray-800">
        <span className="font-medium text-gray-500">
          {t("risks.ai.review.summary.score")}
          {": "}
        </span>
        {initialScore} → {finalScore}
      </p>

      <div>
        <p className="text-sm font-medium text-gray-500">
          {t("risks.ai.review.summary.mitigations", {
            count: draft.mitigations.length,
          })}
        </p>
        <ol className="mt-2 space-y-3">
          {draft.mitigations.map((mitigation, index) => (
            <li
              key={mitigation.id || index}
              className="border border-gray-200 rounded-md p-3"
            >
              <p className="text-sm font-medium text-gray-900">
                <span className="text-gray-400 mr-2">{index + 1}.</span>
                {t(`form.mitigations.options.type.${mitigation.type}`)}
                {mitigation.pictogram && (
                  <span className="ml-2 text-xs text-gray-500">
                    {mitigation.pictogram}
                  </span>
                )}
              </p>
              <p className="mt-1 text-sm text-gray-600 whitespace-pre-line">
                {mitigation.description}
              </p>
              <p className="mt-1 text-sm text-gray-600">
                <span className="font-medium text-gray-500">
                  {t("form.mitigations.left_risk")}
                  {": "}
                </span>
                {mitigation.left_risk}
              </p>
              <p className="mt-1 text-sm text-gray-600">
                <span className="font-medium text-gray-500">
                  {t("form.risk.remaining_risk")}
                  {": "}
                </span>
                {calculateScore(mitigation)}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

interface AcceptGateModalProps {
  show: boolean;
  projectId: string;
  hazard: AIHazardShell;
  // The FINAL user-edited draft from the editor's form values — this exact
  // object goes into the accept body.
  draft: AIDraftRisk;
  // Session group label → RiskGroupping label on accept (contract endpoint 5).
  groupLabel: string;
  riskGroups: RiskGroup[];
  projectStandards: string[];
  onClose: () => void;
}

// THE review gate (plan §8): the ONLY place an AI hazard can be accepted
// into project.risks. Accept requires: zero hard validation errors, every
// warning explicitly acknowledged, the user's own fully_mitigated decision
// (the AI value is ALWAYS false), the review checkbox, and no unresolved
// regeneration proposal. Pattern mirrors ConfirmMitigationStatus in
// components/Risk/Edit.tsx.
export const AcceptGateModal = ({
  show,
  projectId,
  hazard,
  draft,
  groupLabel,
  riskGroups,
  projectStandards,
  onClose,
}: AcceptGateModalProps) => {
  const { t } = useTranslation();
  const { track } = useContext(TrackingContext);
  const acceptMutation = useAcceptAIHazard(projectId);

  const [reviewed, setReviewed] = useState(false);
  const [fullyMitigated, setFullyMitigated] = useState(false);
  const [warningsAcknowledged, setWarningsAcknowledged] = useState(false);

  // Gate-time validation re-run on the FINAL draft (plan §8) — the Phase B
  // page validates the persisted draft, the gate validates what is actually
  // being accepted.
  const validation = useMemo(
    () => validateDraft(draft, riskGroups, projectStandards),
    [draft, riskGroups, projectStandards]
  );

  const hasErrors = validation.errors.length > 0;
  const hasWarnings = hasAcknowledgeableWarnings(
    validation,
    hazard.generation_warnings,
    hazard.skipped_categories
  );
  const hasUnresolvedProposal = !!hazard.pending_proposal;
  const isBlocked = hasErrors || hasUnresolvedProposal;

  // Every open starts with a fresh, unchecked gate — acknowledgments never
  // carry over between reviews.
  useEffect(() => {
    if (show) {
      setReviewed(false);
      setFullyMitigated(false);
      setWarningsAcknowledged(false);
    }
  }, [show]);

  const hasTrackedBlocked = useRef(false);
  useEffect(() => {
    if (show && isBlocked && !hasTrackedBlocked.current) {
      hasTrackedBlocked.current = true;
      track("ai risk: accept blocked", {
        reason: hasErrors ? "validation_errors" : "pending_proposal",
      });
    }

    if (!show) {
      hasTrackedBlocked.current = false;
    }
  }, [show, isBlocked, hasErrors, track]);

  const canAccept =
    !isBlocked &&
    reviewed &&
    (!hasWarnings || warningsAcknowledged) &&
    !acceptMutation.isLoading;

  const handleAccept = () => {
    if (!canAccept) return;

    acceptMutation.mutate({
      hazardId: hazard.id,
      request: {
        // The draft keeps fully_mitigated=false (AI never sets it); the
        // user's decision travels in the dedicated top-level field.
        risk: draft,
        fully_mitigated: fullyMitigated,
        acknowledged: true,
        group_label: groupLabel,
      },
      had_warnings: hasWarnings,
    });
  };

  return (
    <Modal
      show={show}
      onClose={onClose}
      width="1/2"
      content={
        <div>
          <span className="text-xl">{t("risks.ai.review.headline")}</span>

          {/* 1. Scrollable read-only render of everything being accepted */}
          <div className="mt-4 max-h-80 overflow-y-auto border border-gray-200 rounded-md p-4">
            <DraftSummary draft={draft} />
          </div>

          {/* 2. Validator + model warnings / hard errors */}
          <div className="mt-4">
            <WarningsPanel
              validation={validation}
              generationWarnings={hazard.generation_warnings}
              skippedCategories={hazard.skipped_categories}
            />
          </div>

          {hasWarnings && (
            <div className="mt-4 flex items-center h-5">
              <input
                id="ai-accept-warnings"
                type="checkbox"
                checked={warningsAcknowledged}
                onChange={(event) =>
                  setWarningsAcknowledged(event.target.checked)
                }
                className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-cyan-600 text-cyan-600"
              />
              <div className="ml-3 text-sm">
                <label htmlFor="ai-accept-warnings">
                  {t("risks.ai.warnings.acknowledge")}
                </label>
              </div>
            </div>
          )}

          {/* 3. fully_mitigated is the USER's decision (risks.fully_mitigated
              semantics, mirroring ConfirmMitigationStatus) */}
          <div className="mt-6">
            <span className="text-sm font-medium text-gray-900">
              {t("risks.fully_mitigated.headline")}
            </span>
            <div className="text-sm pt-2 pb-2">
              <Trans i18nKey="risks.fully_mitigated.explanation">
                {/* NOTE: don't change the markup here. It would mess with the Translation */}
                <div></div>
                <div className="font-bold py-2"></div>
              </Trans>
            </div>
            <div className="flex items-center h-5">
              <input
                id="ai-accept-fully-mitigated"
                type="checkbox"
                checked={fullyMitigated}
                onChange={(event) => setFullyMitigated(event.target.checked)}
                className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-cyan-600 text-cyan-600"
              />
              <div className="ml-3 text-sm">
                <label htmlFor="ai-accept-fully-mitigated">
                  {t("risks.fully_mitigated.checkbox_label")}
                </label>
              </div>
            </div>
          </div>

          {/* 4. Explicit review acknowledgment */}
          <div className="mt-6 flex items-center h-5">
            <input
              id="ai-accept-review"
              type="checkbox"
              checked={reviewed}
              onChange={(event) => setReviewed(event.target.checked)}
              className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-cyan-600 text-cyan-600"
            />
            <div className="ml-3 text-sm">
              <label htmlFor="ai-accept-review">
                {t("risks.ai.review.checkbox")}
              </label>
            </div>
          </div>

          {isBlocked && (
            <p className="mt-4 text-sm text-red-600">
              {t("risks.ai.review.blocked")}
            </p>
          )}

          {/* 5. Accept only when everything above is satisfied */}
          <div className="pt-6 flex justify-end items-center gap-4">
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              {t("risks.ai.review.keepEditing.cta")}
            </button>
            <PrimaryButton
              testId="ai-accept"
              onClick={handleAccept}
              disabled={!canAccept}
            >
              {t("risks.ai.review.accept.cta")}
            </PrimaryButton>
          </div>
        </div>
      }
    />
  );
};
